import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import { CURRENCIES } from '../utils/pricing';

type CurrencyContextType = {
  currency: (typeof CURRENCIES)[number]['code'];
  setCurrency: (currency: (typeof CURRENCIES)[number]['code']) => void;
};

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Map language-region codes to currencies
const LOCALE_TO_CURRENCY: Record<string, (typeof CURRENCIES)[number]['code']> = {
  // English variants — the base `en` entry catches every region not listed
  // explicitly (bare en, en-IN, en-SG, en-ZA, en-PH, …) → USD.
  en: 'usd',
  'en-GB': 'gbp', // United Kingdom
  'en-AU': 'aud', // Australia
  'en-NZ': 'nzd', // New Zealand
  'en-CA': 'cad', // Canada
  'en-IE': 'eur', // Ireland
  'en-US': 'usd', // United States (default for English)

  // European languages
  de: 'eur', // German -> EUR
  'de-DE': 'eur',
  'de-AT': 'eur',
  'de-CH': 'eur',
  fr: 'eur', // French -> EUR
  'fr-FR': 'eur',
  'fr-BE': 'eur',
  'fr-LU': 'eur',
  'fr-CH': 'eur', // Switzerland (CHF unsupported)
  'fr-CA': 'cad', // Canada
  it: 'eur', // Italian -> EUR
  'it-IT': 'eur',
  es: 'eur', // Spanish -> EUR (Spain); Latin America overrides below -> USD
  'es-ES': 'eur',
  'es-419': 'usd', // Latin America (generic)
  'es-MX': 'usd',
  'es-AR': 'usd',
  'es-CO': 'usd',
  'es-CL': 'usd',
  'es-PE': 'usd',
  pt: 'eur', // Portuguese -> EUR (Portugal)
  'pt-PT': 'eur',
  'pt-BR': 'usd', // Brazil (BRL unsupported)
  nl: 'eur', // Dutch -> EUR
  'nl-NL': 'eur',
  'nl-BE': 'eur',
  pl: 'eur', // Polish -> EUR
  'pl-PL': 'eur',
  sv: 'eur', // Swedish -> EUR
  'sv-SE': 'eur',
  ca: 'eur', // Catalan -> EUR (Spain, Andorra)
  'ca-ES': 'eur',
  'ca-AD': 'eur',
  da: 'eur', // Danish -> EUR
  'da-DK': 'eur',
  fi: 'eur', // Finnish -> EUR
  'fi-FI': 'eur',
  el: 'eur', // Greek -> EUR
  'el-GR': 'eur',
  cs: 'eur', // Czech -> EUR
  'cs-CZ': 'eur',
  hu: 'eur', // Hungarian -> EUR
  'hu-HU': 'eur',
  ro: 'eur', // Romanian -> EUR
  'ro-RO': 'eur',
  sk: 'eur', // Slovak -> EUR
  'sk-SK': 'eur',
  bg: 'eur', // Bulgarian -> EUR
  'bg-BG': 'eur',
  hr: 'eur', // Croatian -> EUR
  'hr-HR': 'eur',
  sl: 'eur', // Slovenian -> EUR
  'sl-SI': 'eur',
  et: 'eur', // Estonian -> EUR
  'et-EE': 'eur',
  lv: 'eur', // Latvian -> EUR
  'lv-LV': 'eur',
  lt: 'eur', // Lithuanian -> EUR
  'lt-LT': 'eur',
  mt: 'eur', // Maltese -> EUR
  'mt-MT': 'eur',
  ga: 'eur', // Irish -> EUR
  'ga-IE': 'eur',

  // Norwegian
  no: 'nok',
  nb: 'nok',
  nn: 'nok',
  'no-NO': 'nok',
  'nb-NO': 'nok',

  // Japanese
  ja: 'jpy',
  'ja-JP': 'jpy',

  // Other common languages without a supported local currency -> USD as the
  // international anchor (base-language entries cover all their regions).
  zh: 'usd', // Chinese
  ko: 'usd', // Korean
  hi: 'usd', // Hindi
  ar: 'usd', // Arabic
  he: 'usd', // Hebrew
  tr: 'usd', // Turkish
  ru: 'usd', // Russian
  id: 'usd', // Indonesian
  ms: 'usd', // Malay
  vi: 'usd', // Vietnamese
  th: 'usd', // Thai
};

function detectCurrencyFromLanguage(): (typeof CURRENCIES)[number]['code'] {
  // Try to get the most specific locale first
  const browserLanguage = navigator.language; // e.g., "en-US", "en-GB"

  // Check exact match first (e.g., "en-GB")
  if (LOCALE_TO_CURRENCY[browserLanguage]) {
    console.log(
      `[Currency] Detected language ${browserLanguage}, setting ${LOCALE_TO_CURRENCY[browserLanguage].toUpperCase()}`,
    );
    return LOCALE_TO_CURRENCY[browserLanguage];
  }

  // Try base language (e.g., "en" from "en-US")
  const baseLanguage = browserLanguage.split('-')[0];
  if (LOCALE_TO_CURRENCY[baseLanguage]) {
    console.log(
      `[Currency] Detected base language ${baseLanguage}, setting ${LOCALE_TO_CURRENCY[baseLanguage].toUpperCase()}`,
    );
    return LOCALE_TO_CURRENCY[baseLanguage];
  }

  // Default to EUR when the language gives no clear signal
  console.log(`[Currency] Language ${browserLanguage} not mapped, defaulting to EUR`);
  return 'eur';
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState(() => {
    return detectCurrencyFromLanguage();
  });

  // Memoized because this provider wraps the whole app: a fresh object here
  // republishes the context to every consumer on any render of this component.
  // `setCurrency` is a `useState` setter, so it is already stable.
  const value = useMemo(() => ({ currency, setCurrency }), [currency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}
